import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Avatar } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Mail, Phone, Building2, PhoneCall, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function Directory() {
  const { user } = useAuth();
  const canEdit = user && ["admin", "hr_admin"].includes(user.role);
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/directory").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((e) => [e.name, e.preferred_name, e.department, e.email, e.work_phone]
      .filter(Boolean).some((v) => v.toLowerCase().includes(s)));
  }, [rows, q]);

  const startEdit = (e) => { setEditId(e.id); setDraft(e.work_phone || ""); };
  const cancelEdit = () => { setEditId(null); setDraft(""); };
  const saveWorkPhone = async (e) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/employees/${e.id}/work-phone`, { work_phone: draft });
      setRows((rs) => rs.map((r) => (r.id === e.id ? { ...r, work_phone: data.work_phone } : r)));
      toast.success(data.work_phone ? "Direct dial saved" : "Direct dial removed");
      cancelEdit();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="directory-page">
      <PageHeader title="People directory" subtitle="Find colleagues across CASS by name, team, email or direct dial." />
      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…" className="pl-9" data-testid="directory-search" />
      </div>
      {rows === null ? (
        <p className="text-slate-400 text-sm" data-testid="directory-loading">Loading directory…</p>
      ) : (
        <>
          <p className="text-xs text-slate-400" data-testid="directory-count">{filtered.length} {filtered.length === 1 ? "person" : "people"}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((e) => (
              <div key={e.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow flex items-start gap-3" data-testid={`directory-card-${e.id}`}>
                <Avatar name={e.name} photo={e.photo_url} size={48} />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[#0B1A2F] truncate">{e.preferred_name || e.name}</p>
                  {e.department && <p className="text-xs text-slate-500 flex items-center gap-1 truncate"><Building2 size={12} />{e.department}</p>}
                  {e.email && <a href={`mailto:${e.email}`} className="text-xs text-[#2EA3FD] hover:underline flex items-center gap-1 truncate" data-testid={`directory-email-${e.id}`}><Mail size={12} />{e.email}</a>}
                  {e.phone && <p className="text-xs text-slate-400 flex items-center gap-1 truncate"><Phone size={12} />{e.phone}</p>}

                  {editId === e.id ? (
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <PhoneCall size={12} className="text-slate-400 shrink-0" />
                      <Input value={draft} onChange={(ev) => setDraft(ev.target.value)} placeholder="e.g. +44 20 7946 0000"
                        className="h-7 text-xs" autoFocus data-testid={`directory-workphone-input-${e.id}`}
                        onKeyDown={(ev) => { if (ev.key === "Enter") saveWorkPhone(e); if (ev.key === "Escape") cancelEdit(); }} />
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" disabled={saving} onClick={() => saveWorkPhone(e)} data-testid={`directory-workphone-save-${e.id}`}><Check size={14} /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400" onClick={cancelEdit} data-testid={`directory-workphone-cancel-${e.id}`}><X size={14} /></Button>
                    </div>
                  ) : e.work_phone ? (
                    <p className="text-xs text-[#0B1A2F] font-medium flex items-center gap-1 truncate mt-0.5" data-testid={`directory-workphone-${e.id}`}>
                      <PhoneCall size={12} className="text-[#2EA3FD]" />
                      <a href={`tel:${e.work_phone.replace(/\s+/g, "")}`} className="hover:underline">{e.work_phone}</a>
                      <span className="text-slate-400 font-normal">· direct dial</span>
                      {canEdit && <button onClick={() => startEdit(e)} className="text-slate-300 hover:text-[#2EA3FD]" data-testid={`directory-workphone-edit-${e.id}`}><Pencil size={11} /></button>}
                    </p>
                  ) : canEdit ? (
                    <button onClick={() => startEdit(e)} className="mt-1 text-xs text-slate-400 hover:text-[#2EA3FD] flex items-center gap-1" data-testid={`directory-workphone-add-${e.id}`}>
                      <PhoneCall size={12} />Add direct dial
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          {filtered.length === 0 && <p className="text-slate-400 text-sm" data-testid="directory-empty">No one matches “{q}”.</p>}
        </>
      )}
    </div>
  );
}
